import { useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Group as GroupIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GroupStatusBadge } from './GroupStatusBadge'
import type { StudentGroup } from '@/types'

interface GroupCardProps {
  group: StudentGroup
  onEdit?: (group: StudentGroup) => void
  onDelete?: (group: StudentGroup) => void
  onAddMember?: (group: StudentGroup) => void
  onArchive?: (group: StudentGroup) => void
  onRestore?: (group: StudentGroup) => void
}

export function GroupCard({
  group,
  onEdit,
  onDelete,
  onAddMember,
  onArchive,
  onRestore,
}: GroupCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleCardClick = () => {
    navigate(`/groups/${group.id}`)
  }

  const handleEdit = () => {
    handleMenuClose()
    onEdit?.(group)
  }

  const handleDelete = () => {
    handleMenuClose()
    onDelete?.(group)
  }

  const handleAddMember = () => {
    handleMenuClose()
    onAddMember?.(group)
  }

  const handleArchive = () => {
    handleMenuClose()
    onArchive?.(group)
  }

  const handleRestore = () => {
    handleMenuClose()
    onRestore?.(group)
  }

  const handleView = () => {
    handleMenuClose()
    navigate(`/groups/${group.id}`)
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              mr: 2,
              width: 48,
              height: 48,
            }}
          >
            <GroupIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" component="h3">
                {group.name}
              </Typography>
              <GroupStatusBadge status={group.status} label={group.status_label} />
            </Box>
            {group.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {group.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <GroupIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {t('groups.membersCount')}: {group.members_count}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ ml: 'auto' }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          {t('common.view')}
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          {t('common.edit')}
        </MenuItem>
        <MenuItem onClick={handleAddMember}>
          <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
          {t('groups.addMember')}
        </MenuItem>
        {group.status === 'active' ? (
          <MenuItem onClick={handleArchive}>
            <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
            {t('groups.archive')}
          </MenuItem>
        ) : (
          <MenuItem onClick={handleRestore}>
            <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
            {t('groups.restore')}
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          {t('common.delete')}
        </MenuItem>
      </Menu>
    </Card>
  )
}
